/**
 * Query Service - AI Agent Query API
 *
 * Exposes a simple API for AI agents (like Claude for Chrome) to query
 * the in-memory DuckDB instance containing intercepted CSV data.
 */

import type * as duckdb from '@duckdb/duckdb-wasm';

/**
 * Information about a dataset/table in the database
 */
export interface DatasetInfo {
  /** Table name in the database */
  name: string;
  /** Number of rows in the table */
  rowCount: number;
  /** Column names */
  columns: string[];
  /** Source of the data (e.g., "intercepted from api.example.com") */
  source?: string;
}

/**
 * Result of a SQL query
 */
export interface QueryResult {
  /** Column names */
  columns: string[];
  /** Row data as 2D array */
  rows: any[][];
  /** Total number of rows returned */
  rowCount: number;
  /** Error message if query failed */
  error?: string;
}

/**
 * Column information for table schema
 */
export interface ColumnInfo {
  /** Column name */
  name: string;
  /** Column data type */
  type: string;
}

/**
 * Query Service API for AI agents
 */
export interface QueryServiceAPI {
  /** Check if the service is ready */
  isReady(): boolean;
  /** List all available datasets/tables */
  listDatasets(): Promise<DatasetInfo[]>;
  /** Execute a SQL query and return results */
  query(sql: string): Promise<QueryResult>;
  /** Get schema for a specific table */
  getSchema(tableName: string): Promise<ColumnInfo[]>;
}

/**
 * Create a query service instance
 *
 * @param getDb - Function to get the DuckDB instance
 * @returns QueryServiceAPI instance
 */
export function createQueryService(
  getDb: () => duckdb.AsyncDuckDB | null
): QueryServiceAPI {
  return {
    isReady(): boolean {
      return getDb() !== null;
    },

    async listDatasets(): Promise<DatasetInfo[]> {
      const db = getDb();
      if (!db) {
        throw new Error('Database not initialized. Call isReady() first.');
      }

      const conn = await db.connect();
      try {
        // Get all tables in the main schema
        const tablesResult = await conn.query(`
          SELECT table_name
          FROM information_schema.tables
          WHERE table_schema = 'main'
            AND table_type = 'BASE TABLE'
        `);

        const datasets: DatasetInfo[] = [];

        for (let i = 0; i < tablesResult.numRows; i++) {
          const tableName = tablesResult.getChildAt(0)?.get(i) as string;

          // Get row count
          const countResult = await conn.query(
            `SELECT COUNT(*) as cnt FROM "${tableName}"`
          );
          const rowCount = Number(countResult.getChildAt(0)?.get(0)) || 0;

          // Get column names
          const columnsResult = await conn.query(`
            SELECT column_name
            FROM information_schema.columns
            WHERE table_schema = 'main'
              AND table_name = '${tableName}'
            ORDER BY ordinal_position
          `);

          const columns: string[] = [];
          for (let j = 0; j < columnsResult.numRows; j++) {
            columns.push(columnsResult.getChildAt(0)?.get(j) as string);
          }

          datasets.push({
            name: tableName,
            rowCount,
            columns,
          });
        }

        return datasets;
      } finally {
        await conn.close();
      }
    },

    async query(sql: string): Promise<QueryResult> {
      const db = getDb();
      if (!db) {
        return {
          columns: [],
          rows: [],
          rowCount: 0,
          error: 'Database not initialized. Call isReady() first.',
        };
      }

      const conn = await db.connect();
      try {
        const result = await conn.query(sql);
        const columns = result.schema.fields.map((f) => f.name);
        const rows: any[][] = [];

        for (let i = 0; i < result.numRows; i++) {
          const row: any[] = [];
          for (let j = 0; j < columns.length; j++) {
            row.push(result.getChildAt(j)?.get(i));
          }
          rows.push(row);
        }

        return {
          columns,
          rows,
          rowCount: result.numRows,
        };
      } catch (err: any) {
        return {
          columns: [],
          rows: [],
          rowCount: 0,
          error: err.message || 'Query failed',
        };
      } finally {
        await conn.close();
      }
    },

    async getSchema(tableName: string): Promise<ColumnInfo[]> {
      const db = getDb();
      if (!db) {
        throw new Error('Database not initialized. Call isReady() first.');
      }

      const conn = await db.connect();
      try {
        const result = await conn.query(`
          SELECT column_name, data_type
          FROM information_schema.columns
          WHERE table_schema = 'main'
            AND table_name = '${tableName}'
          ORDER BY ordinal_position
        `);

        const columns: ColumnInfo[] = [];
        for (let i = 0; i < result.numRows; i++) {
          columns.push({
            name: result.getChildAt(0)?.get(i) as string,
            type: result.getChildAt(1)?.get(i) as string,
          });
        }

        return columns;
      } finally {
        await conn.close();
      }
    },
  };
}
